import { CreditCard, Info, AlertTriangle, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { format } from "date-fns";
import { formatPrice } from "../../lib/utils";
import type { CheckoutParticipant } from "../../lib/checkout-participant";

interface CheckoutSummaryProps {
  totalEvents: number;
  totalParticipants: number;
  total: number;
  currency: string;
  depositInfo: {
    totalDeposit: number;
    balances: Array<{ date: Date; amount: number }>;
  } | null;
  allParticipantsComplete: boolean;
  participants: CheckoutParticipant[];
  user: { email: string | null } | null | undefined;
  bookingEmailAddress: string;
  marketingConsent: boolean;
  onBookingEmailChange: (email: string) => void;
  onMarketingConsentChange: (consent: boolean) => void;
  onProceedToCheckout: () => void;
  isProcessing: boolean;
}

export function CheckoutSummary({
  totalEvents,
  totalParticipants,
  total,
  currency,
  depositInfo,
  allParticipantsComplete,
  participants,
  user,
  bookingEmailAddress,
  marketingConsent,
  onBookingEmailChange,
  onMarketingConsentChange,
  onProceedToCheckout,
  isProcessing,
}: CheckoutSummaryProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Checkout Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Events</span>
            <span>{totalEvents}</span>
          </div>
          <div className="flex justify-between">
            <span>Attendees</span>
            <span>{totalParticipants}</span>
          </div>
          <div className="border-t pt-2">
            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span>{formatPrice(total, currency)}</span>
            </div>
          </div>
          {depositInfo && (
            <div className="mt-4 pt-4 border-t border-dashed space-y-2 text-sm">
              <div className="flex justify-between text-gray-900 font-medium">
                <span>Due now</span>
                <span>{formatPrice(depositInfo.totalDeposit, currency)}</span>
              </div>
              {depositInfo.balances.map((balance, idx) => (
                <div
                  key={idx}
                  className="flex justify-between text-gray-600"
                >
                  <span>Due on {format(balance.date, "MMM do, yyyy")}</span>
                  <span>{formatPrice(balance.amount, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2 text-blue-800">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">
                  Payment Information
                </p>
                <p className="text-sm text-blue-700">
                  You will not be charged until your booking is approved.
                </p>
              </div>
            </div>
          </div>
        )}

        {!allParticipantsComplete && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">
                {participants.some((p) => p.hasExpiredConsents)
                  ? "Consent Review Required"
                  : "Forms Incomplete"}
              </span>
            </div>
            <p className="text-sm text-orange-700 mt-1">
              {participants.some((p) => p.hasExpiredConsents)
                ? "One or more participants have expired consents. Please review and update them before checkout."
                : "Please complete all required forms before checkout."}
            </p>
          </div>
        )}

        <div className="space-y-4 pt-4 border-t">
          {!user && (
            <>
              <div>
                <label
                  htmlFor="bookingEmail"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="bookingEmail"
                  value={bookingEmailAddress}
                  onChange={(e) => onBookingEmailChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="your.email@example.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  We'll send booking confirmation to this email
                </p>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="marketing"
                    checked={marketingConsent}
                    onChange={(e) => onMarketingConsentChange(e.target.checked)}
                    className="mt-1 rounded border-gray-300"
                  />
                  <div>
                    <label
                      htmlFor="marketing"
                      className="text-sm font-medium"
                    >
                      Marketing Communications (Optional)
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      I would like to receive information about future events
                      and club activities.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="space-y-3">
          <Button
            className="w-full"
            onClick={onProceedToCheckout}
            disabled={
              !allParticipantsComplete ||
              isProcessing ||
              (!user && !bookingEmailAddress)
            }
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {isProcessing ? "Processing..." : "Checkout"}
          </Button>
          <Link
            to="/policies/booking-policy"
            className="flex items-center justify-center text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            <FileText className="w-4 h-4 mr-1" />
            View Booking Policy
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}


